/**
 * Utility for Snapchat Pixel tracking
 */

// Check if snaptr is available
const isSnapchatAvailable = () => {
  return typeof window !== 'undefined' && typeof window.snaptr === 'function';
};

/**
 * Hash string using SHA-256
 * @param {string} str - String to hash
 * @returns {string} - Hashed string
 */
const sha256 = async (str) => {
  // Use the Web Crypto API which is supported in all modern browsers
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert the hash to a hex string
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Get user identification parameters
 * @returns {object} Common user identification parameters
 */
const getUserIdentifiers = async () => {
  // Attempt to get phone number from localStorage
  const phoneNo = localStorage.getItem('phoneNo') || '';
  
  // Properly hash the phone number using SHA-256
  let hashedPhone = '';
  if (phoneNo) {
    try {
      hashedPhone = await sha256(phoneNo);
    } catch (error) {
      console.error("Error hashing phone number for Snapchat pixel:", error);
    }
  }
  
  // Get user UUID if stored in localStorage
  const uuid = localStorage.getItem('uuid_c1') || '';
  
  return {
    'uuid_c1': uuid,
    'user_phone_number': phoneNo,
    'user_hashed_phone_number': hashedPhone
  };
};

/**
 * Track a standard event with Snapchat Pixel
 * @param {string} eventName - Name of the event to track
 * @param {object} eventParams - Optional parameters for the event
 */
const trackEvent = async (eventName, eventParams = {}) => {
  if (isSnapchatAvailable()) {
    try {
      // Add user identifiers to all events
      const userIdentifiers = await getUserIdentifiers();
      const params = {
        ...eventParams,
        ...userIdentifiers
      };
      
      window.snaptr('track', eventName, params);
      console.log(`Snapchat Pixel: ${eventName} event tracked`, params);
    } catch (error) {
      console.error(`Snapchat Pixel: Failed to track ${eventName} event`, error);
    }
  } else {
    console.warn("Snapchat Pixel not available");
  }
};

/**
 * Track ADD_CART event (when a user adds an item to their cart)
 * @param {object} product - Product data
 */
const trackAddToCart = async (product) => {
  const eventParams = {
    item_ids: [product.subscription_plan_id || product.id],
    price: product.discounted_cost || product.price || 0,
    currency: product.currency_type || 'INR',
    item_category: product.category || '',
    number_items: 1
  };
  
  await trackEvent('ADD_CART', eventParams);
};

/**
 * Track VIEW_CONTENT event (when a user views a product)
 * @param {object} product - Product data
 */
const trackViewContent = async (product) => {
  const eventParams = {
    item_ids: [product.subscription_plan_id || product.id],
    price: product.discounted_cost || product.price || 0,
    currency: product.currency_type || 'INR',
    item_category: product.category || ''
  };
  
  await trackEvent('VIEW_CONTENT', eventParams);
};

/**
 * Track START_CHECKOUT event
 * @param {object} cartData - Cart data
 */
const trackStartCheckout = async (cartData) => {
  // Extract item IDs from cart
  const itemIds = (cartData.items || []).map(item => 
    item.subscription_plan?.subscription_plan_id || item.id
  );
  
  const eventParams = {
    item_ids: itemIds.length > 0 ? itemIds : ['default_item'],
    currency: cartData.currency_type || 'INR',
    number_items: cartData.items?.length || 1,
    price: cartData.total_amount || cartData.cost || 0
  };
  
  await trackEvent('START_CHECKOUT', eventParams);
};

/**
 * Track PURCHASE event
 * @param {object} orderData - Order data
 */
const trackPurchase = async (orderData) => {
  // Extract item IDs from order
  const itemIds = (orderData.new_ordered_items || orderData.items || []).map(item => 
    item.subscription_plan?.subscription_plan_id || item.id
  );
  
  const eventParams = {
    currency: orderData.details?.currency_type || orderData.currency_type || 'INR',
    price: orderData.payment?.amount || orderData.total_amount || 0,
    transaction_id: orderData.order_id || orderData.id,
    item_ids: itemIds.length > 0 ? itemIds : ['default_item'],
    number_items: itemIds.length || 1,
    item_category: orderData.category || '',
    user_email: orderData.email || ''
  };
  
  await trackEvent('PURCHASE', eventParams);
};

/**
 * Track LOGIN event
 * @param {object} userData - User data (optional)
 */
const trackLogin = async (userData = {}) => {
  await trackEvent('LOGIN', userData);
};

/**
 * Track custom PAGE_VIEW with item information
 * @param {object} pageData - Page data with item information
 */
const trackPageViewWithItems = async (pageData = {}) => {
  const eventParams = {
    item_ids: pageData.item_ids || [],
    item_category: pageData.item_category || ''
  };
  
  await trackEvent('PAGE_VIEW', eventParams);
};

export default {
  trackEvent,
  trackAddToCart,
  trackViewContent,
  trackStartCheckout,
  trackPurchase,
  trackLogin,
  trackPageViewWithItems
}; 